/**
 * Loader for the public runtime config JSON.
 *
 * The flow:
 *   1. parseUrlParams — pull publisherId / quizId from window.location.search
 *   2. fetchConfig     — fetch {VITE_QUIZ_CDN_URL}/{VITE_QUIZ_CDN_KEY_PREFIX}/{pub}/{id}.json
 *   3. validateConfig  — hand-rolled type guards confirming the parsed JSON
 *                        is a well-formed RuntimeQuizConfig
 *
 * All three return Result<T, LoaderError>. Errors carry enough detail to
 * log to console; src/runtime/errors.ts maps them to user-facing copy.
 *
 * Bundle-size note: we deliberately avoid Zod (~12 KB gz) and valibot (~3 KB
 * gz) here. The schema is bounded (~10 leaf shapes) and the validation
 * paths are clearer for our specific error surface as hand-rolled guards.
 * Revisit if the schema 3-4x in complexity (per CLAUDE.md).
 */

import {
  type AnyRuntimeImageOption,
  type AnyRuntimeStep,
  type RuntimeChatBotConfig,
  type RuntimeChatLastMessageCta,
  type RuntimeChatMessage,
  type RuntimeChatQuickReply,
  type RuntimeChatStep,
  type RuntimeDelivery,
  type RuntimeFooterLink,
  type RuntimeHeader,
  type RuntimeImageStep,
  type RuntimeLeadDestination,
  type RuntimePgTrigger,
  type RuntimeQuestionOption,
  type RuntimeQuestionStep,
  type RuntimeQuizConfig,
  type RuntimeScratchCardPreOption,
  type RuntimeScratchCardPreQuestion,
  type RuntimeScratchCardResultCta,
  type RuntimeScratchCardStep,
  type RuntimeSettings,
  type RuntimeSpinWheelSegment,
  type RuntimeSpinWheelStep,
  SUPPORTED_SCHEMA_VERSION,
} from '@/types/runtime';

// ───────────────────────────────────────────────────────────────────
// Result + LoaderError
// ───────────────────────────────────────────────────────────────────

export type Result<T, E> = { ok: true; value: T } | { ok: false; error: E };

export type LoaderError =
  | { kind: 'missing_params'; details: string }
  | { kind: 'not_found' }
  | { kind: 'network_error'; status: number | null; details: string }
  | { kind: 'parse_error'; details: string }
  | { kind: 'schema_version_mismatch'; received: string; supported: string }
  | { kind: 'validation_error'; path: string; details: string };

export interface QuizParams {
  publisherId: string;
  quizId: string;
}

const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
const err = <E>(error: E): Result<never, E> => ({ ok: false, error });

// ───────────────────────────────────────────────────────────────────
// parseUrlParams
// ───────────────────────────────────────────────────────────────────

const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export function parseUrlParams(search: string): Result<QuizParams, LoaderError> {
  const params = new URLSearchParams(search);
  const publisherId = params.get('pub');
  const quizId = params.get('id');

  if (!publisherId) {
    return err({ kind: 'missing_params', details: 'pub query param is required' });
  }
  if (!quizId) {
    return err({ kind: 'missing_params', details: 'id query param is required' });
  }
  if (!ID_PATTERN.test(publisherId)) {
    return err({ kind: 'missing_params', details: `pub contains invalid characters: ${publisherId}` });
  }
  if (!ID_PATTERN.test(quizId)) {
    return err({ kind: 'missing_params', details: `id contains invalid characters: ${quizId}` });
  }

  return ok({ publisherId, quizId });
}

// ───────────────────────────────────────────────────────────────────
// fetchConfig
// ───────────────────────────────────────────────────────────────────

export async function fetchConfig(p: QuizParams): Promise<Result<unknown, LoaderError>> {
  const cdn = import.meta.env.VITE_QUIZ_CDN_URL;
  if (!cdn) {
    return err({
      kind: 'network_error',
      status: null,
      details: 'VITE_QUIZ_CDN_URL is not configured',
    });
  }

  // Must match pga-service's `QUIZ_S3_KEY_PREFIX` exactly — they're both
  // pointing at the same bucket layout. Dev / staging:  quiz/test_config
  // Prod:                                                quiz/config
  // Required env var; refuse rather than silently fall back so a misconfigured
  // staging build can't accidentally read or paint over production keys.
  const keyPrefix = (import.meta.env.VITE_QUIZ_CDN_KEY_PREFIX ?? '').replace(/^\/+|\/+$/g, '');
  if (!keyPrefix) {
    return err({
      kind: 'network_error',
      status: null,
      details: 'VITE_QUIZ_CDN_KEY_PREFIX is not configured',
    });
  }

  const url = `${cdn.replace(/\/$/, '')}/${keyPrefix}/${encodeURIComponent(p.publisherId)}/${encodeURIComponent(p.quizId)}.json`;

  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', credentials: 'omit' });
  } catch (e) {
    return err({
      kind: 'network_error',
      status: null,
      details: `network failure: ${e instanceof Error ? e.message : String(e)}`,
    });
  }

  if (res.status === 404) {
    return err({ kind: 'not_found' });
  }
  if (!res.ok) {
    return err({
      kind: 'network_error',
      status: res.status,
      details: `non-2xx response: ${res.status} ${res.statusText}`,
    });
  }

  try {
    const json = await res.json();
    return ok(json);
  } catch (e) {
    return err({
      kind: 'parse_error',
      details: `JSON parse failed: ${e instanceof Error ? e.message : String(e)}`,
    });
  }
}

// ───────────────────────────────────────────────────────────────────
// validateConfig — hand-rolled type guards
// ───────────────────────────────────────────────────────────────────

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isString(v: unknown): v is string {
  return typeof v === 'string';
}
function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function isInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v);
}
function isBoolean(v: unknown): v is boolean {
  return typeof v === 'boolean';
}
function isArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

const fail = (path: string, details: string): Result<never, LoaderError> =>
  err({ kind: 'validation_error', path, details });

function getField(o: Record<string, unknown>, key: string): unknown {
  return Object.hasOwn(o, key) ? o[key] : undefined;
}

// ── leaf shape validators ──

function validateHeader(v: unknown, path: string): Result<RuntimeHeader, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const title = getField(v, 'title');
  const subtitle = getField(v, 'subtitle');
  const logoUrl = getField(v, 'logoUrl');
  if (!isString(title)) return fail(`${path}.title`, 'expected string');
  if (!isString(subtitle)) return fail(`${path}.subtitle`, 'expected string');
  if (!isString(logoUrl)) return fail(`${path}.logoUrl`, 'expected string');
  return ok({ title, subtitle, logoUrl });
}

function validateSettings(v: unknown, path: string): Result<RuntimeSettings, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const primaryColor = getField(v, 'primaryColor');
  const backgroundColor = getField(v, 'backgroundColor');
  const progressBar = getField(v, 'progressBar');
  const ctaText = getField(v, 'ctaText');
  const disclaimer = getField(v, 'disclaimer');
  const buttonColor = getField(v, 'buttonColor');
  if (!isString(primaryColor)) return fail(`${path}.primaryColor`, 'expected string');
  if (!isString(backgroundColor)) return fail(`${path}.backgroundColor`, 'expected string');
  if (!isBoolean(progressBar)) return fail(`${path}.progressBar`, 'expected boolean');
  if (!isString(ctaText)) return fail(`${path}.ctaText`, 'expected string');
  if (!isString(disclaimer)) return fail(`${path}.disclaimer`, 'expected string');
  if (buttonColor !== undefined && !isString(buttonColor)) {
    return fail(`${path}.buttonColor`, 'expected string');
  }
  const normalizedButtonColor =
    buttonColor === undefined || buttonColor.trim() === '' ? primaryColor : buttonColor;
  return ok({
    primaryColor,
    backgroundColor,
    progressBar,
    ctaText,
    disclaimer,
    buttonColor: normalizedButtonColor,
  });
}

function validateDelivery(v: unknown, path: string): Result<RuntimeDelivery, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const url = getField(v, 'url');
  const openMode = getField(v, 'openMode');
  if (!isString(url)) return fail(`${path}.url`, 'expected string');
  if (!isString(openMode)) return fail(`${path}.openMode`, 'expected string');
  return ok({ url, openMode });
}

function validatePgTrigger(v: unknown, path: string): Result<RuntimePgTrigger, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const enabled = getField(v, 'enabled');
  const placement = getField(v, 'placement');
  const disclaimerText = getField(v, 'disclaimerText');
  if (!isBoolean(enabled)) return fail(`${path}.enabled`, 'expected boolean');
  if (!isString(placement)) return fail(`${path}.placement`, 'expected string');
  if (!isString(disclaimerText)) return fail(`${path}.disclaimerText`, 'expected string');
  return ok({ enabled, placement, disclaimerText });
}

function validateLeadDestination(
  v: unknown,
  path: string,
): Result<RuntimeLeadDestination, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const mode = getField(v, 'mode');
  if (mode !== 'server_side') {
    return fail(`${path}.mode`, `expected literal "server_side", got ${JSON.stringify(mode)}`);
  }
  return ok({ mode });
}

function validateFooterLink(v: unknown, path: string): Result<RuntimeFooterLink, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const label = getField(v, 'label');
  const url = getField(v, 'url');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  if (!isString(url)) return fail(`${path}.url`, 'expected string');
  return ok({ label, url });
}

function validateArray<T>(
  v: unknown,
  path: string,
  itemValidator: (item: unknown, itemPath: string) => Result<T, LoaderError>,
): Result<T[], LoaderError> {
  if (!isArray(v)) return fail(path, 'expected array');
  const out: T[] = [];
  for (let i = 0; i < v.length; i++) {
    const r = itemValidator(v[i], `${path}[${i}]`);
    if (!r.ok) return r;
    out.push(r.value);
  }
  return ok(out);
}

// ── step option validators ──

function validateQuestionOption(
  v: unknown,
  path: string,
): Result<RuntimeQuestionOption, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const label = getField(v, 'label');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  return ok({ id, label });
}

function validateImageOption(
  v: unknown,
  path: string,
): Result<AnyRuntimeImageOption, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const kind = getField(v, 'kind');
  const label = getField(v, 'label');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');

  if (kind === 'image_option') {
    const imageUrl = getField(v, 'imageUrl');
    if (!isString(imageUrl)) return fail(`${path}.imageUrl`, 'expected string');
    return ok({ id, kind, label, imageUrl });
  }
  if (kind === 'button_option') {
    const backgroundColor = getField(v, 'backgroundColor');
    const textColor = getField(v, 'textColor');
    if (!isString(backgroundColor)) return fail(`${path}.backgroundColor`, 'expected string');
    if (!isString(textColor)) return fail(`${path}.textColor`, 'expected string');
    return ok({ id, kind, label, backgroundColor, textColor });
  }
  return fail(`${path}.kind`, `expected "image_option" | "button_option", got ${JSON.stringify(kind)}`);
}

function validateSpinWheelSegment(
  v: unknown,
  path: string,
): Result<RuntimeSpinWheelSegment, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const label = getField(v, 'label');
  const color = getField(v, 'color');
  const icon = getField(v, 'icon');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  if (!isString(color)) return fail(`${path}.color`, 'expected string');
  if (!isString(icon)) return fail(`${path}.icon`, 'expected string');
  return ok({ id, label, color, icon });
}

function validateScratchPreOption(
  v: unknown,
  path: string,
): Result<RuntimeScratchCardPreOption, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const label = getField(v, 'label');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  return ok({ id, label });
}

function validateScratchPreQuestion(
  v: unknown,
  path: string,
): Result<RuntimeScratchCardPreQuestion, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const title = getField(v, 'title');
  if (!isString(title)) return fail(`${path}.title`, 'expected string');
  const opts = validateArray(getField(v, 'options'), `${path}.options`, validateScratchPreOption);
  if (!opts.ok) return opts;
  return ok({ title, options: opts.value });
}

function validateScratchResultCta(
  v: unknown,
  path: string,
): Result<RuntimeScratchCardResultCta, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const label = getField(v, 'label');
  const url = getField(v, 'url');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  if (!isString(url)) return fail(`${path}.url`, 'expected string');
  return ok({ label, url });
}

function validateChatQuickReply(
  v: unknown,
  path: string,
): Result<RuntimeChatQuickReply, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const label = getField(v, 'label');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  return ok({ id, label });
}

function validateChatMessage(v: unknown, path: string): Result<RuntimeChatMessage, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const sender = getField(v, 'sender');
  const text = getField(v, 'text');
  const typingDelay = getField(v, 'typingDelay');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(sender)) return fail(`${path}.sender`, 'expected string');
  if (!isString(text)) return fail(`${path}.text`, 'expected string');
  if (!isInteger(typingDelay)) return fail(`${path}.typingDelay`, 'expected integer');
  const quickReplies = validateArray(
    getField(v, 'quickReplies'),
    `${path}.quickReplies`,
    validateChatQuickReply,
  );
  if (!quickReplies.ok) return quickReplies;
  return ok({ id, sender, text, typingDelay, quickReplies: quickReplies.value });
}

function validateChatBotConfig(
  v: unknown,
  path: string,
): Result<RuntimeChatBotConfig, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const name = getField(v, 'name');
  const avatarUrl = getField(v, 'avatarUrl');
  const statusText = getField(v, 'statusText');
  const headerColor = getField(v, 'headerColor');
  if (!isString(name)) return fail(`${path}.name`, 'expected string');
  if (!isString(avatarUrl)) return fail(`${path}.avatarUrl`, 'expected string');
  if (!isString(statusText)) return fail(`${path}.statusText`, 'expected string');
  if (!isString(headerColor)) return fail(`${path}.headerColor`, 'expected string');
  return ok({ name, avatarUrl, statusText, headerColor });
}

function validateChatLastMessageCta(
  v: unknown,
  path: string,
): Result<RuntimeChatLastMessageCta, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const label = getField(v, 'label');
  const url = getField(v, 'url');
  if (!isString(label)) return fail(`${path}.label`, 'expected string');
  if (!isString(url)) return fail(`${path}.url`, 'expected string');
  return ok({ label, url });
}

// ── step validators (dispatched by type) ──

function validateQuestionStep(
  v: Record<string, unknown>,
  path: string,
  id: string,
  index: number,
): Result<RuntimeQuestionStep, LoaderError> {
  const title = getField(v, 'title');
  const subtitle = getField(v, 'subtitle');
  if (!isString(title)) return fail(`${path}.title`, 'expected string');
  if (!isString(subtitle)) return fail(`${path}.subtitle`, 'expected string');
  const options = validateArray(getField(v, 'options'), `${path}.options`, validateQuestionOption);
  if (!options.ok) return options;
  return ok({ id, type: 'question', index, title, subtitle, options: options.value });
}

function validateImageStep(
  v: Record<string, unknown>,
  path: string,
  id: string,
  index: number,
): Result<RuntimeImageStep, LoaderError> {
  const title = getField(v, 'title');
  if (!isString(title)) return fail(`${path}.title`, 'expected string');
  const options = validateArray(getField(v, 'options'), `${path}.options`, validateImageOption);
  if (!options.ok) return options;
  return ok({ id, type: 'image', index, title, options: options.value });
}

function validateSpinWheelStep(
  v: Record<string, unknown>,
  path: string,
  id: string,
  index: number,
): Result<RuntimeSpinWheelStep, LoaderError> {
  const preSpinTitle = getField(v, 'preSpinTitle');
  const spinButtonText = getField(v, 'spinButtonText');
  const resultButtonText = getField(v, 'resultButtonText');
  if (!isString(preSpinTitle)) return fail(`${path}.preSpinTitle`, 'expected string');
  if (!isString(spinButtonText)) return fail(`${path}.spinButtonText`, 'expected string');
  if (!isString(resultButtonText)) return fail(`${path}.resultButtonText`, 'expected string');
  const segments = validateArray(
    getField(v, 'segments'),
    `${path}.segments`,
    validateSpinWheelSegment,
  );
  if (!segments.ok) return segments;
  return ok({
    id,
    type: 'spin_wheel',
    index,
    preSpinTitle,
    spinButtonText,
    resultButtonText,
    segments: segments.value,
  });
}

function validateScratchCardStep(
  v: Record<string, unknown>,
  path: string,
  id: string,
  index: number,
): Result<RuntimeScratchCardStep, LoaderError> {
  const title = getField(v, 'title');
  const subtitle = getField(v, 'subtitle');
  const preQuestionRaw = getField(v, 'preQuestion');
  const scratchPrompt = getField(v, 'scratchPrompt');
  const revealText = getField(v, 'revealText');
  const footerNote = getField(v, 'footerNote');
  const scratchPercent = getField(v, 'scratchPercent');
  const brushWidth = getField(v, 'brushWidth');
  if (!isString(title)) return fail(`${path}.title`, 'expected string');
  if (!isString(subtitle)) return fail(`${path}.subtitle`, 'expected string');
  if (!isString(scratchPrompt)) return fail(`${path}.scratchPrompt`, 'expected string');
  if (!isString(revealText)) return fail(`${path}.revealText`, 'expected string');
  if (!isString(footerNote)) return fail(`${path}.footerNote`, 'expected string');
  if (!isNumber(scratchPercent)) return fail(`${path}.scratchPercent`, 'expected number');
  if (!isInteger(brushWidth)) return fail(`${path}.brushWidth`, 'expected integer');

  // Normalize scratchPercent to a 0..1 fraction. The runtime contract is
  // fractional (matches ratioCleared()'s 0..1 output and the fixture's
  // 0.5), but Studio's editor mapper stores it as a 0..100 percentage and
  // both Studio's preview serializer and pga-service pass it through
  // unchanged — so values >1 reach us in production. Detecting "looks
  // like a percentage" and dividing is safer than asserting, given that
  // 50 vs 0.5 produces a silently-unreachable threshold instead of a
  // loud error. Clamp into [0, 1] regardless so a stray 1.5 or -0.1
  // can't break the reveal check.
  // TODO: drop this once Studio + pga-service serializers divide by 100
  // at their boundary (their UI shape is already 0..1).
  const normalizedScratchPercent = Math.max(
    0,
    Math.min(1, scratchPercent > 1 ? scratchPercent / 100 : scratchPercent),
  );

  let preQuestion: RuntimeScratchCardStep['preQuestion'] = null;
  if (preQuestionRaw !== null && preQuestionRaw !== undefined) {
    const pq = validateScratchPreQuestion(preQuestionRaw, `${path}.preQuestion`);
    if (!pq.ok) return pq;
    preQuestion = pq.value;
  }

  const resultCta = validateScratchResultCta(getField(v, 'resultCta'), `${path}.resultCta`);
  if (!resultCta.ok) return resultCta;

  return ok({
    id,
    type: 'scratch_card',
    index,
    title,
    subtitle,
    preQuestion,
    scratchPrompt,
    revealText,
    resultCta: resultCta.value,
    footerNote,
    scratchPercent: normalizedScratchPercent,
    brushWidth,
  });
}

function validateChatStep(
  v: Record<string, unknown>,
  path: string,
  id: string,
  index: number,
): Result<RuntimeChatStep, LoaderError> {
  const botConfig = validateChatBotConfig(getField(v, 'botConfig'), `${path}.botConfig`);
  if (!botConfig.ok) return botConfig;
  const messages = validateArray(getField(v, 'messages'), `${path}.messages`, validateChatMessage);
  if (!messages.ok) return messages;

  const lastCtaRaw = getField(v, 'lastMessageCta');
  let lastMessageCta: RuntimeChatStep['lastMessageCta'] = null;
  if (lastCtaRaw !== null && lastCtaRaw !== undefined) {
    const r = validateChatLastMessageCta(lastCtaRaw, `${path}.lastMessageCta`);
    if (!r.ok) return r;
    lastMessageCta = r.value;
  }

  return ok({
    id,
    type: 'chat',
    index,
    botConfig: botConfig.value,
    messages: messages.value,
    lastMessageCta,
  });
}

function validateStep(v: unknown, path: string): Result<AnyRuntimeStep, LoaderError> {
  if (!isObject(v)) return fail(path, 'expected object');
  const id = getField(v, 'id');
  const type = getField(v, 'type');
  const index = getField(v, 'index');
  if (!isString(id)) return fail(`${path}.id`, 'expected string');
  if (!isString(type)) return fail(`${path}.type`, 'expected string');
  if (!isInteger(index)) return fail(`${path}.index`, 'expected integer');

  switch (type) {
    case 'question':
      return validateQuestionStep(v, path, id, index);
    case 'image':
      return validateImageStep(v, path, id, index);
    case 'spin_wheel':
      return validateSpinWheelStep(v, path, id, index);
    case 'scratch_card':
      return validateScratchCardStep(v, path, id, index);
    case 'chat':
      return validateChatStep(v, path, id, index);
    case 'lead_capture':
      // DEFERRED-V21: pga-service's union supports lead_capture; v2.0 runtime
      // deliberately refuses it. When v2.1 lands, add a case here and a step
      // renderer module under src/steps/lead-capture.ts.
      return fail(
        `${path}.type`,
        'lead_capture is DEFERRED-V21 — runtime does not render this step type in v2.0',
      );
    default:
      return fail(`${path}.type`, `unknown step type ${JSON.stringify(type)}`);
  }
}

// ── top-level validator ──

export function validateConfig(json: unknown): Result<RuntimeQuizConfig, LoaderError> {
  if (!isObject(json)) {
    return fail('$', 'expected object at root');
  }

  const schemaVersion = getField(json, 'schemaVersion');
  if (!isString(schemaVersion)) return fail('$.schemaVersion', 'expected string');
  if (schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
    return err({
      kind: 'schema_version_mismatch',
      received: schemaVersion,
      supported: SUPPORTED_SCHEMA_VERSION,
    });
  }

  const publisherId = getField(json, 'publisherId');
  const quizId = getField(json, 'quizId');
  const revision = getField(json, 'revision');
  const status = getField(json, 'status');
  const locale = getField(json, 'locale');
  if (!isString(publisherId)) return fail('$.publisherId', 'expected string');
  if (!isString(quizId)) return fail('$.quizId', 'expected string');
  if (!isInteger(revision)) return fail('$.revision', 'expected integer');
  if (status !== 'active' && status !== 'inactive') {
    return fail('$.status', `expected "active" | "inactive", got ${JSON.stringify(status)}`);
  }
  if (!isString(locale)) return fail('$.locale', 'expected string');

  const header = validateHeader(getField(json, 'header'), '$.header');
  if (!header.ok) return header;
  const settings = validateSettings(getField(json, 'settings'), '$.settings');
  if (!settings.ok) return settings;
  const delivery = validateDelivery(getField(json, 'delivery'), '$.delivery');
  if (!delivery.ok) return delivery;
  const pgTrigger = validatePgTrigger(getField(json, 'pgTrigger'), '$.pgTrigger');
  if (!pgTrigger.ok) return pgTrigger;
  const leadDestination = validateLeadDestination(
    getField(json, 'leadDestination'),
    '$.leadDestination',
  );
  if (!leadDestination.ok) return leadDestination;
  const footerLinks = validateArray(
    getField(json, 'footerLinks'),
    '$.footerLinks',
    validateFooterLink,
  );
  if (!footerLinks.ok) return footerLinks;
  const steps = validateArray(getField(json, 'steps'), '$.steps', validateStep);
  if (!steps.ok) return steps;

  return ok({
    schemaVersion,
    publisherId,
    quizId,
    revision,
    status,
    locale,
    header: header.value,
    settings: settings.value,
    delivery: delivery.value,
    pgTrigger: pgTrigger.value,
    leadDestination: leadDestination.value,
    footerLinks: footerLinks.value,
    steps: steps.value,
  });
}

// ───────────────────────────────────────────────────────────────────
// loadQuiz — convenience composition used by main.ts
// ───────────────────────────────────────────────────────────────────

export async function loadQuiz(
  search: string,
): Promise<Result<RuntimeQuizConfig, LoaderError>> {
  const params = parseUrlParams(search);
  if (!params.ok) return params;

  const fetched = await fetchConfig(params.value);
  if (!fetched.ok) return fetched;

  return validateConfig(fetched.value);
}
